import React, { useState, useEffect, useRef } from 'react'

interface TruncatedTextProps {
  text: string
  maxLines?: number
  moreText?: string
  tailText?: string
  className?: string
  textClassName?: string
  moreTextClassName?: string
  tailTextClassName?: string
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  maxLines = 3,
  moreText = '... 더 보기',
  tailText = '',
  className = '',
  textClassName = '',
  moreTextClassName = '',
  tailTextClassName = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const hiddenRef = useRef<HTMLParagraphElement>(null)
  const measureRef = useRef<HTMLParagraphElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [displayText, setDisplayText] = useState(text)

  useEffect(() => {
    setDisplayText(text)
    
    if (!text || !hiddenRef.current || !containerRef.current || !measureRef.current) {
      setIsOverflowing(false)
      return
    }

    const checkTextOverflow = () => {
      if (!hiddenRef.current || !containerRef.current || !measureRef.current) return

      const hiddenElement = hiddenRef.current
      const container = containerRef.current
      const measureElement = measureRef.current

      const containerWidth = container.offsetWidth
      if (containerWidth > 0) {
        hiddenElement.style.width = `${containerWidth}px`
        measureElement.style.width = `${containerWidth}px`
        measureElement.style.wordBreak = 'break-word'
      }

      const fullHeight = hiddenElement.scrollHeight

      const computedStyle = getComputedStyle(hiddenElement)
      const fontSize = parseFloat(computedStyle.fontSize)
      const lineHeight = parseFloat(computedStyle.lineHeight) || fontSize * 1.8
      const maxHeight = lineHeight * maxLines

      const isOverflowing = fullHeight > maxHeight + 1

      setIsOverflowing(isOverflowing)

      if (isOverflowing) {
        let low = 0
        let high = text.length
        let bestFit = ''

        // 이진 탐색으로 3줄에 맞는 텍스트 길이 찾기
        while (low <= high) {
          const mid = Math.floor((low + high) / 2)
          const testText = text.substring(0, mid)
          
          // 측정 요소에 텍스트 + moreText + tailText 설정 (인라인으로)
          const tempSpan = document.createElement('span')
          tempSpan.textContent = testText
          const tempMoreSpan = document.createElement('span')
          tempMoreSpan.textContent = moreText
          tempMoreSpan.className = moreTextClassName
          measureElement.innerHTML = ''
          measureElement.appendChild(tempSpan)
          measureElement.appendChild(tempMoreSpan)
          if (tailText) {
            const tempTailSpan = document.createElement('span')
            tempTailSpan.textContent = tailText
            tempTailSpan.className = tailTextClassName
            measureElement.appendChild(tempTailSpan)
          }
          
          // 강제로 리플로우 발생시키기
          void measureElement.offsetHeight

          const testHeight = measureElement.scrollHeight

          if (testHeight <= maxHeight + 1) {
            bestFit = testText
            low = mid + 1
          } else {
            high = mid - 1
          }
        }

        setDisplayText(bestFit || text)
      } else {
        setDisplayText(text)
      }
    }

    const timeoutId = setTimeout(checkTextOverflow, 0)

    window.addEventListener('resize', checkTextOverflow)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', checkTextOverflow)
    }
  }, [text, maxLines, moreText, tailText, moreTextClassName, tailTextClassName])

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* 숨겨진 요소: 전체 텍스트 높이 측정용 */}
      <p
        ref={hiddenRef}
        className={`absolute top-0 left-0 w-full ${textClassName}`}
        style={{ 
          wordBreak: 'break-word',
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1
        }}
      >
        {text}
      </p>
      {/* 텍스트 측정용 요소 (보이지 않음) */}
      <p
        ref={measureRef}
        className={`absolute top-0 left-0 w-full ${textClassName}`}
        style={{ 
          wordBreak: 'break-word',
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1
        }}
      />
      {/* 표시용 요소 */}
      {isOverflowing ? (
        <p className={`${textClassName}`} style={{ wordBreak: 'break-word' }}>
          {displayText}
          <span className={`inline ${moreTextClassName}`}>{moreText}</span>
          {tailText && (
            <span className={`inline ${tailTextClassName}`}>{tailText}</span>
          )}
        </p>
      ) : (
        <p className={textClassName} style={{ wordBreak: 'break-word' }}>
          {displayText}
          {tailText && (
            <span className={`inline ${tailTextClassName}`}>{tailText}</span>
          )}
        </p>
      )}
    </div>
  )
}

export default TruncatedText
